import { useState } from 'react';
import { Calculator, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

export const Simulator = () => {
  const { language, t } = useLanguage();
  const [values, setValues] = useState({
    orders: '',
    aov: '',
    abandonment: '70',
    recovery: '3',
  });
  const [result, setResult] = useState(null);

  const formatCurrency = (value) => {
    if (language === 'pt') {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleChange = (field, value) => {
    // Only allow numbers and decimal point
    const cleaned = value.replace(/[^0-9.]/g, '');
    setValues((prev) => ({ ...prev, [field]: cleaned }));
    setResult(null);
  };

  const calculate = () => {
    const orders = parseFloat(values.orders) || 0;
    const aov = parseFloat(values.aov) || 0;
    const abandonment = parseFloat(values.abandonment) || 0;
    const recovery = parseFloat(values.recovery) || 0;

    if (orders <= 0 || aov <= 0) {
      return;
    }

    // Calculate potential revenue
    const abandonedCarts = orders * (abandonment / 100);
    const recoveredCarts = abandonedCarts * (recovery / 100);
    const baseRevenue = recoveredCarts * aov;

    // Create range with ±30% variation
    const min = Math.round(baseRevenue * 0.7);
    const max = Math.round(baseRevenue * 1.3);

    setResult({ min, max });
  };

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-cyan-500/10 rounded-lg">
          <Calculator className="w-6 h-6 text-cyan-400" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-slate-100">{t.simulator.title}</h3>
          <p className="text-sm text-slate-400">{t.simulator.subtitle}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <Label htmlFor="sim-orders" className="text-slate-300 text-sm mb-2 block">
            {t.simulator.ordersLabel}
          </Label>
          <Input
            id="sim-orders"
            type="text"
            inputMode="numeric"
            placeholder={t.simulator.ordersPlaceholder}
            value={values.orders}
            onChange={(e) => handleChange('orders', e.target.value)}
            className="bg-slate-950 border-slate-700 text-slate-100 placeholder:text-slate-500 h-12"
            data-testid="simulator-orders"
          />
        </div>

        <div>
          <Label htmlFor="sim-aov" className="text-slate-300 text-sm mb-2 block">
            {t.simulator.aovLabel}
          </Label>
          <Input
            id="sim-aov"
            type="text"
            inputMode="numeric"
            placeholder={t.simulator.aovPlaceholder}
            value={values.aov}
            onChange={(e) => handleChange('aov', e.target.value)}
            className="bg-slate-950 border-slate-700 text-slate-100 placeholder:text-slate-500 h-12"
            data-testid="simulator-aov"
          />
        </div>

        <div>
          <Label htmlFor="sim-abandonment" className="text-slate-300 text-sm mb-2 block">
            {t.simulator.abandonmentLabel}
          </Label>
          <Input
            id="sim-abandonment"
            type="text"
            inputMode="numeric"
            placeholder={t.simulator.abandonmentPlaceholder}
            value={values.abandonment}
            onChange={(e) => handleChange('abandonment', e.target.value)}
            className="bg-slate-950 border-slate-700 text-slate-100 placeholder:text-slate-500 h-12"
            data-testid="simulator-abandonment"
          />
        </div>

        <div>
          <Label htmlFor="sim-recovery" className="text-slate-300 text-sm mb-2 block">
            {t.simulator.recoveryLabel}
          </Label>
          <Input
            id="sim-recovery"
            type="text"
            inputMode="numeric"
            placeholder={t.simulator.recoveryPlaceholder}
            value={values.recovery}
            onChange={(e) => handleChange('recovery', e.target.value)}
            className="bg-slate-950 border-slate-700 text-slate-100 placeholder:text-slate-500 h-12"
            data-testid="simulator-recovery"
          />
        </div>
      </div>

      <Button
        onClick={calculate}
        className="w-full bg-cyan-500 text-slate-950 hover:bg-cyan-400 font-bold rounded-full h-12"
        data-testid="simulator-calculate"
      >
        {t.simulator.calculateButton}
      </Button>

      {result && (
        <div className="mt-6 p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl">
          <p className="text-sm text-slate-400 mb-2">{t.simulator.resultTitle}</p>
          <p className="text-2xl lg:text-3xl font-bold text-cyan-400">
            {t.simulator.resultRange
              .replace('{min}', formatCurrency(result.min))
              .replace('{max}', formatCurrency(result.max))}
          </p>
          <p className="text-sm text-slate-400">{t.simulator.resultMonthly}</p>
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-400 mb-1">
              {t.simulator.disclaimerTitle}
            </p>
            <p className="text-xs text-slate-400 leading-relaxed">
              {t.simulator.disclaimerText}
            </p>
          </div>
        </div>
      </div>

      {/* Assumptions */}
      <div className="mt-4">
        <p className="text-xs text-slate-500 font-medium mb-2">{t.simulator.assumptionsTitle}</p>
        <ul className="text-xs text-slate-500 space-y-1">
          <li>• {t.simulator.assumption1}</li>
          <li>• {t.simulator.assumption2}</li>
          <li>• {t.simulator.assumption3}</li>
        </ul>
      </div>
    </div>
  );
};
